<template>
	<div
		v-if="modelValue"
		class="modal fade show d-block station-picker"
		tabindex="-1"
		role="dialog"
		@click.self="close"
	>
		<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-geo-alt me-2"></i>Add station
					</h5>
					<button
						type="button"
						class="btn-close"
						:disabled="saving"
						@click="close"
					></button>
				</div>

				<div class="modal-body">
					<div v-if="resolving" class="text-center py-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading…</span>
						</div>
						<p class="mt-2 mb-0 text-muted small">Reading the parcel…</p>
					</div>

					<template v-else>
						<!-- The parcel the click resolved, with the address, owner and
						     property type the county states. A click that hit no
						     parcel (or a parcel the district cannot resolve) says so,
						     because the site is then created from the point's own
						     reverse-geocoded address rather than from the parcel. -->
						<template v-if="resolved">
							<dl class="row mb-0 small">
								<dt class="col-sm-4 text-muted">Parcel</dt>
								<dd class="col-sm-8">
									{{ resolved.external_id }} ({{ resolved.region }})
								</dd>

								<dt class="col-sm-4 text-muted">Address</dt>
								<dd class="col-sm-8">{{ addressText || "—" }}</dd>

								<dt class="col-sm-4 text-muted">Owner</dt>
								<dd class="col-sm-8">{{ resolved.situs?.owner || "—" }}</dd>

								<dt class="col-sm-4 text-muted">Property type</dt>
								<dd class="col-sm-8">{{ propertyTypeText }}</dd>
							</dl>

							<p
								v-if="!addressText"
								class="alert alert-warning mt-3 mb-0 small"
							>
								This parcel states no address, so the site will be created from
								the address of the point clicked.
							</p>
						</template>

						<p
							v-else-if="parcel"
							class="alert alert-warning small mb-0"
							role="alert"
						>
							The parcel {{ parcel.external_id }} could not be read, so the site
							will be created from the address of the point clicked and will not
							be linked to the parcel.
						</p>

						<p v-else class="text-muted small mb-0">
							No parcel is on file at this point, so the site will be created
							from the address of the point clicked.
						</p>

						<!-- Whose site the station stands at: an existing site on the
						     parcel (any one of them — the parcel may hold several) or a
						     new one. -->
						<div class="mt-3">
							<h6 class="small text-uppercase text-muted">Site</h6>

							<div
								v-for="choice in siteChoices"
								:key="choice.id"
								class="form-check"
							>
								<input
									:id="`stationSite${choice.id}`"
									v-model="chosenSiteID"
									class="form-check-input"
									type="radio"
									:value="choice.id"
									:disabled="saving"
								/>
								<label
									class="form-check-label"
									:for="`stationSite${choice.id}`"
								>
									{{ choice.label }}
								</label>
							</div>

							<div class="form-check">
								<input
									id="stationNewSite"
									v-model="chosenSiteID"
									class="form-check-input"
									type="radio"
									:value="null"
									:disabled="saving"
								/>
								<label class="form-check-label" for="stationNewSite">
									{{
										resolved
											? "Use a new site on this parcel"
											: "Use a new site at this point"
									}}
								</label>
							</div>
						</div>

						<div v-if="error" class="alert alert-danger mt-3 mb-0" role="alert">
							{{ error }}
						</div>
					</template>
				</div>

				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-outline-secondary"
						:disabled="saving"
						@click="close"
					>
						Cancel
					</button>
					<ButtonLoading
						text="Add station"
						icon="bi bi-plus-lg"
						variant="primary"
						:loading="saving"
						:disabled="!point || resolving"
						@click="confirm"
					/>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show"></div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { formatAddress } from "@/format";
import type {
	Address,
	MapReviewTrapStationRow,
	ParcelResolveResponse,
	Site,
	SiteCreateRequest,
	SiteCreateResponse,
} from "@/type/api";

/** The point the operator clicked: where the station will stand. */
interface Point {
	latitude: number;
	longitude: number;
}

/** The parcel the click hit, when it hit one. */
interface ParcelRef {
	external_id: string;
	region: string;
}

const props = defineProps<{
	modelValue: boolean;
	point: Point | null;
	parcel: ParcelRef | null;
}>();

const emit = defineEmits<{
	"update:modelValue": [value: boolean];
	created: [stationID: number];
}>();

const resolving = ref(false);
const saving = ref(false);
const error = ref("");
// The parcel's own record, or null when the click hit no parcel (or the
// district holds no parcel for it) — the flow then creates the site from the
// point's reverse-geocoded address.
const resolved = ref<ParcelResolveResponse | null>(null);
const siteChoices = ref<{ id: number; label: string }[]>([]);
// The site the station stands at: an existing site's id, or null for "use a
// new site".
const chosenSiteID = ref<number | null>(null);

/** The parcel's address as the county states it: the situs street address when
 * the record has one, else the parcel's raw address string. Empty when neither
 * exists, which is what makes the site come from the clicked point. */
const addressText = computed(() => {
	const situs = resolved.value?.situs;
	const street = [situs?.address_number, situs?.address_street]
		.filter((part) => !!part)
		.join(" ");
	const locality = situs?.address_locality ?? "";
	const line = [street, locality].filter((part) => !!part).join(", ");
	return line || situs?.address_raw || resolved.value?.address_raw || "";
});

const propertyTypeText = computed(
	() =>
		resolved.value?.situs?.property_type ||
		resolved.value?.property_type ||
		"—",
);

/** A Site's name as the rest of the app reads it: its label when that reads as
 * a name, else its address, else its id (Instances.vue's siteLabel). A parcel
 * Site is labelled with its APN, which is an identifier rather than a name. */
function siteLabel(site: Site): string {
	if (site.label && /[A-Za-z]/.test(site.label)) return site.label;
	if (hasAddress(site.address)) return formatAddress(site.address);
	return `Site #${site.id}`;
}

function hasAddress(address?: Address): boolean {
	return !!address && (!!address.number || !!address.street);
}

function close() {
	if (saving.value) return;
	emit("update:modelValue", false);
}

// Escape always dismisses the dialog (no stuck shade).
function onKeydown(event: KeyboardEvent) {
	if (event.key === "Escape" && props.modelValue) close();
}
watch(
	() => props.modelValue,
	(open) => {
		if (open) window.addEventListener("keydown", onKeydown);
		else window.removeEventListener("keydown", onKeydown);
	},
	{ immediate: true },
);
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

/** The sites the parcel already carries, named for the operator. A site whose
 * own read fails keeps its id, so the parcel never looks site-less when it is
 * not — creating a duplicate would be refused with a 409 anyway. */
async function loadSiteChoices(ids: number[]) {
	if (ids.length === 0) return;
	const loaded = await Promise.all(
		ids.map((id) =>
			apiClient
				.JSONGet<Site>(`/api/site/${id}`)
				.then((site) => siteLabel(site))
				.catch(() => `Site #${id}`),
		),
	);
	siteChoices.value = ids.map((id, index) => ({ id, label: loaded[index] }));
	// The parcel already has a site, so that is the answer the flow needs: a
	// new site on the same parcel is refused (409). The operator can still pick
	// "new site" deliberately, and the refusal explains itself if they do.
	chosenSiteID.value = ids[0];
}

// A new dialog state each time it opens, keyed on the point and parcel it was
// opened for.
watch(
	() => [props.modelValue, props.point, props.parcel] as const,
	async ([open]) => {
		if (!open) return;
		resolved.value = null;
		siteChoices.value = [];
		chosenSiteID.value = null;
		error.value = "";

		const parcel = props.parcel;
		if (!parcel?.external_id) return;

		resolving.value = true;
		try {
			resolved.value = await apiClient.JSONGet<ParcelResolveResponse>(
				`/api/municipal/parcel-resolve/${encodeURIComponent(
					parcel.external_id,
				)}?region=${encodeURIComponent(parcel.region)}`,
			);
			await loadSiteChoices(resolved.value.site_ids ?? []);
		} catch {
			// No parcel record to show — the click's own point is still enough to
			// create a site from, so the dialog offers exactly that.
			resolved.value = null;
		} finally {
			resolving.value = false;
		}
	},
	{ immediate: true },
);

/** Create (or reuse) the site, then set the station on it. The parcel is named
 * by the record the server resolved, never by the click's raw properties: the
 * pair is what the site's `site_parcel` link is keyed on. */
async function confirm() {
	const point = props.point;
	if (!point || saving.value || resolving.value) return;

	saving.value = true;
	error.value = "";
	try {
		let siteID = chosenSiteID.value;
		if (siteID === null) {
			const body: SiteCreateRequest = {
				latitude: point.latitude,
				longitude: point.longitude,
			};
			if (resolved.value) {
				body.region = resolved.value.region;
				body.external_id = resolved.value.external_id;
			}
			const site = await apiClient.JSONPost<SiteCreateResponse>(
				"/api/site",
				body,
			);
			siteID = site.site.id;
		}

		const station = await apiClient.JSONPost<
			MapReviewTrapStationRow & { permission: string }
		>("/api/trap-station", {
			site_id: siteID,
			latitude: point.latitude,
			longitude: point.longitude,
		});
		emit("created", station.id);
		emit("update:modelValue", false);
	} catch (e: any) {
		// The server's refusals land here: a point outside the site's parcel, an
		// address that could not be resolved (400), and a parcel that already has
		// a site (409).
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to add the station. Please try again.";
	} finally {
		saving.value = false;
	}
}
</script>

<style scoped>
.modal-dialog {
	z-index: 1060;
}

.modal-backdrop {
	z-index: 1050;
}
</style>
